/* ============================================
BOOST CUSTOMISATION START
PDP Bulk Order Grid — assets/boost-bulk-grid.js  (session 3, KEY-ADDRESSED REWRITE 2026-05-23)

KEY-ADDRESSED REWRITE (2026-05-23):
  Fixes the cart-corruption bug where editing an existing decorated
  size split the row into carrier-less "(unspecified)" lines, mis-set
  other sizes' carriers, and zeroed the $50 fee. Root cause: every
  qty mutation used a POSITIONAL /cart/update.js array built from a
  stale snapshot, plus whole-row carrier arithmetic that forced the
  summed quantity onto a carrier shared (by variant id) across pk
  groups. Confirmed by live cart dumps (2026-05-23).

  Fix: address every cart line by its immutable Shopify line `key`,
  never by position. Keys are unique per line even when variant ids
  collide (the exact case that defeated the positional model: two
  embroidery carriers, same variant id, different keys).

  - rebuildDecoratedState captures, per size, the backing pk group(s)
    with each line's `key` (groupsBySize) and plain lines' keys
    (plainKeysByVariantId).
  - EDIT existing size  → keyed /cart/update.js (updates object keyed
    by line key) touching ONLY that size's group lines. Decoration
    carriers track garment qty; sample-confirmation carriers stay 1.
  - ADD new size        → /cart/add.js with a fresh pk + cloned
    carriers + once-per-order fees (unchanged; already correct).
  - REMOVE row          → keyed /cart/update.js zeroing every backing
    group line by key (no positional array).
  - Behavioural model (option 3, confirmed): one pk group per unique
    colour+size+decoration-set; adding more of an existing size
    increments that group by key rather than spawning a duplicate.

  The interim hard input-lock (2026-05-23) is REMOVED here: the race
  is gone structurally, so input stays responsive.

  COMMIT-ON-EXIT MODEL (2026-05-23): the grid commits a cart change
  ONLY when the customer leaves a cell (blur / Tab / Enter), then a
  500ms settle delay batches a fast tab-through. Nothing commits while
  the cursor is in a cell, and an external cart event never repaints a
  focused cell. This fixes the "typed 4, cell blanked, typed 4 again →
  44" mis-entry. Enter commits and blurs. An empty cell on blur is an
  intentional clear (qty 0); the row is retained with its X control
  when the whole row reaches zero. See attachCellListeners, SETTLE_MS,
  and the focused-cell guard in refreshFromCart.

  Performance note: editing is now one keyed call, not a whole-cart
  positional rewrite, and merging repeat adds keeps the cart smaller.
  A dedicated performance pass (shared single cart read per mutation,
  debounced section refresh) is recommended separately — see the
  design doc.

V2.1 changes (from v2 testing 2026-05-16):
  - X-button drawer-open fix: dispatch 'themeCartOpened' event directly
    after the cart update completes. The ajaxCart.load() openCartPanel
    arg only opens the drawer when the response branch is taken AND
    the panel is in the right state; passing the event ensures the
    drawer opens unconditionally.

V2 architecture (unchanged):
  - Positional /cart/update.js for unambiguous line targeting
  - Aggregation by (colour + decoration label), not _product_key
  - Decoration label read from artwork carrier line item
  - Sticky rows survive qty 0 until X-clicked
============================================ */
(function () {
  'use strict';

  // BOOST CUSTOMISATION 2026-05-23 (COMMIT-ON-EXIT MODEL): SETTLE_MS is the
  // delay AFTER a cell is exited (blur) before the queued change is sent to
  // the cart. It batches a fast tab-through across several cells into fewer
  // cart calls. It is NOT a keystroke debounce — nothing commits while the
  // cursor is in a cell. 500ms per Phil 2026-05-23.
  var SETTLE_MS = 500;
  // Back-compat alias: existing references to DEBOUNCE_MS now use the settle
  // delay. (No keystroke debounce path remains.)
  var DEBOUNCE_MS = SETTLE_MS;
  var POST_MUTATION_RESYNC_DELAY = 100;

  function BoostBulkGrid(rootEl) {
    this.root = rootEl;
    this.productId = Number(rootEl.dataset.boostProductId);
    this.currency = rootEl.dataset.boostCurrency || 'AUD';
    this.colourIdx = Number(rootEl.dataset.boostColourIdx);
    this.sizeIdx = Number(rootEl.dataset.boostSizeIdx);
    this.productKeyPrefix = rootEl.dataset.boostProductKeyPrefix || 'pk-';

    this.decoratedTbody = rootEl.querySelector('[data-boost-bulk-decorated-tbody]');
    this.plainTbody = rootEl.querySelector('[data-boost-bulk-plain-tbody]');
    this.decoratedRowTemplate = rootEl.querySelector('[data-boost-bulk-decorated-row-template]');
    this.unitsValueEl = rootEl.querySelector('[data-boost-bulk-units-value]');
    this.priceValueEl = rootEl.querySelector('[data-boost-bulk-price-value]');

    this.plainInputs = Array.prototype.slice.call(
      rootEl.querySelectorAll('[data-boost-bulk-input][data-boost-bulk-row-type="plain"]')
    );

    var sizeHeaders = rootEl.querySelectorAll('thead .boost-bulk-grid-size-header');
    this.sizeValues = [];
    for (var i = 0; i < sizeHeaders.length; i++) {
      this.sizeValues.push(sizeHeaders[i].textContent.trim());
    }

    this.cartItemsSnapshot = [];
    this.decoratedRowsByAggKey = {};
    this.plainQtyByVariantId = {};
    this.plainLineIndicesByVariantId = {};
    // BOOST CUSTOMISATION 2026-05-23 (KEY-ADDRESSED REWRITE): plain variant id
    // → cart line key(s).
    this.plainKeysByVariantId = {};
    this.stickyAggKeys = {};
    this.stickyRowSnapshots = {};

    this.pendingPositionalUpdates = {};
    // BOOST CUSTOMISATION 2026-05-23 (KEY-ADDRESSED REWRITE): key → quantity
    // map for /cart/change.js. Replaces positional updates for all qty edits
    // (plain + decorated existing-size + remove). Each entry targets ONE cart
    // line by its immutable `key`, so edits never collide across pk groups or
    // zero unrelated lines. pendingPositionalUpdates is retained but no longer
    // populated (kept to avoid touching unrelated references; safe to remove
    // in a later cleanup pass).
    this.pendingKeyChanges = {};
    this.pendingPlainAdds = {};
    // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2): pending adds for NEW
    // sizes on a decorated row. Keyed by aggKey+'|'+size; each entry holds
    // the garment variant, qty, and the decoration carrier variant+method
    // set to clone. Carriers carry no per-garment-unique text (names/numbers
    // are captured post-checkout via the Artwork Order Form), so cloning a
    // decoration to a new size is always safe.
    this.pendingDecoratedAdds = {};
    // BOOST CUSTOMISATION 2026-05-22 (v2.3.0 Stage 2): pending adds for NEW
    // sizes on a SAMPLE-ONLY row (garment + sample-confirmation carrier, no
    // decorations, no fees). Keyed by aggKey+'|'+size.
    this.pendingSampleAdds = {};
    this.debounceTimer = null;
    this.submissionInFlight = false;
    // BOOST CUSTOMISATION 2026-05-23 (STATE/RENDER SPLIT): submission depth
    // counter. submissionInFlight is true whenever _submitDepth > 0, so the
    // flush-level cycle claim and inner per-fetch guards compose without one
    // releasing the other early. See _enterSubmission / _exitSubmission.
    this._submitDepth = 0;
    // BOOST CUSTOMISATION 2026-05-23 (COMMIT-ON-EXIT MODEL): guards a deferred
    // refresh while a grid cell is focused (set in refreshFromCart). The old
    // _lastQueuedValue de-dupe map is removed — with commit-on-blur there is
    // no keystroke commit to de-dupe.
    this._refreshDeferred = false;

    this.init();
  }

  BoostBulkGrid.prototype.init = function () {
    var self = this;
    this.plainInputs.forEach(function (inp) { self.attachCellListeners(inp); });
    this.refreshFromCart();
    document.addEventListener('boost:cart-updated', function () {
      setTimeout(function () { self.refreshFromCart(); }, POST_MUTATION_RESYNC_DELAY);
    });
  };

  BoostBulkGrid.prototype.attachCellListeners = function (inp) {
    var self = this;
    // ============================================================
    // BOOST CUSTOMISATION 2026-05-23 (COMMIT-ON-EXIT MODEL):
    // The grid no longer commits a cart change on every keystroke. A cart
    // update is queued ONLY when the customer leaves the cell (blur), then a
    // settle delay (SETTLE_MS) batches a fast tab-through before sending. This
    // removes the "typed 4, cell blanked, typed 4 again → 44" class of bugs
    // caused by an auto-commit firing mid-entry and repainting the focused
    // cell.
    //
    // Behaviour (confirmed with Phil 2026-05-23):
    //   - `input` (keystroke): updates the live totals readout only. No cart
    //      call, no debounce, no row rebuild. The customer can pause as long
    //      as they like with the cursor in the cell and nothing commits.
    //   - `keydown` Enter: commit and blur (the blur handler does the commit).
    //   - `blur`: queue this cell's value (queueCellUpdate) and arm the
    //      SETTLE_MS timer in flushPendingUpdates' debounce. An empty cell on
    //      blur is treated as an intentional clear → quantity 0 (removes the
    //      backing line if one exists; the row is retained with its X control
    //      when the whole row reaches zero).
    //   - A cell the customer never focuses never blurs, so it is never
    //      touched (tab-through with no edit retains the quantity).
    //
    // The old input/change de-dupe machinery (_lastQueuedValue) is gone: with
    // commit-on-blur there is no keystroke commit to de-dupe against.
    // ============================================================

    // Live-total update on keystroke ONLY (no cart commit).
    inp.addEventListener('input', function () {
      self.recalcTotals();
    });

    // Enter commits and blurs (spreadsheet-style). The actual commit happens
    // in the blur handler that the .blur() call triggers.
    inp.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.keyCode === 13) {
        e.preventDefault();
        inp.blur();
      }
    });

    // Commit on exit. This is the ONLY path that queues a cart change.
    inp.addEventListener('blur', function () {
      // Normalise display: an empty or non-positive value shows blank, and is
      // treated as an intentional clear (qty 0) by queueCellUpdate.
      var qty = parseInt(inp.value, 10);
      if (isNaN(qty) || qty <= 0) inp.value = '';
      self.recalcTotals();
      self.updateRemoveButtonVisibility();
      // Queue the change for this cell, then arm the settle timer.
      self.queueCellUpdate(inp);
    });
  };

  // BOOST CUSTOMISATION 2026-05-23 (STATE/RENDER SPLIT):
  // refreshStateOnly fetches the cart and rebuilds the internal KEY MAPS
  // (cartItemsSnapshot, groupsBySize via rebuildDecoratedState, and the plain
  // key maps) WITHOUT re-rendering the grid and WITHOUT touching any focused
  // input. It is safe to run while a cell is focused. This is what every
  // commit calls so the next edit knows the current line keys — fixing the
  // "reduce 10 to 4 became 14" bug, where the plain edit path didn't know a
  // line already existed (the render-level focus guard had deferred the only
  // state rebuild, leaving plainKeysByVariantId empty so the edit added
  // instead of setting the quantity).
  BoostBulkGrid.prototype.refreshStateOnly = function () {
    var self = this;
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        self.cartItemsSnapshot = (cart && cart.items) || [];
        // rebuildDecoratedState only recomputes internal maps; it does not
        // write to the DOM, so it cannot disturb a focused cell.
        self.rebuildDecoratedState();
      })
      .catch(function (err) {
        console.warn('[boost-bulk-grid] state-only refresh failed:', err);
      });
  };

  BoostBulkGrid.prototype.refreshFromCart = function () {
    // BOOST CUSTOMISATION 2026-05-22 (Fixes Round 2): do not refresh while a
    // settle timer is pending or a submission is in-flight. A mid-flush refresh
    // rebuilds the decorated rows, re-attaches listeners, and can re-apply a
    // stale update. flushPendingUpdates already calls refreshFromCart on
    // completion, so skipping here does not lose any update.
    if (this.submissionInFlight || this.debounceTimer) return Promise.resolve(null);

    // BOOST CUSTOMISATION 2026-05-23 (COMMIT-ON-EXIT MODEL): never REPAINT the
    // grid while the customer has a grid cell focused. An external cart event
    // (e.g. the cart drawer or the cart page in another tab dispatching
    // boost:cart-updated) must not rebuild the table out from under a cell the
    // customer is typing in — that is what blanked the cell and produced the
    // "4 → 44" double entry. Defer the RENDER until focus leaves the grid.
    //
    // NOTE: this guard only defers the visual render. Internal state (the key
    // maps) is kept fresh separately by refreshStateOnly(), which every commit
    // calls and which is safe to run while focused. So deferring the render
    // here no longer starves the edit path of line keys.
    var active = document.activeElement;
    if (active && active.matches && active.matches('[data-boost-bulk-input]') && this.root.contains(active)) {
      var self0 = this;
      if (this._refreshDeferred) return Promise.resolve(null);
      this._refreshDeferred = true;
      var onBlurOnce = function () {
        self0._refreshDeferred = false;
        active.removeEventListener('blur', onBlurOnce);
        // Let the blur's own commit/settle run first, then render.
        setTimeout(function () { self0.refreshFromCart(); }, POST_MUTATION_RESYNC_DELAY);
      };
      active.addEventListener('blur', onBlurOnce);
      return Promise.resolve(null);
    }

    var self = this;
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        self.cartItemsSnapshot = (cart && cart.items) || [];
        self.rebuildDecoratedState();
        self.renderDecoratedRows();
        self.syncPlainInputsFromState();
        self.recalcTotals();
        self.updateRemoveButtonVisibility();
      })
      .catch(function (err) {
        console.warn('[boost-bulk-grid] cart fetch failed:', err);
      });
  };

  BoostBulkGrid.prototype.rebuildDecoratedState = function () {
    var self = this;
    var items = this.cartItemsSnapshot;

    // ==================================================================
    // BOOST CUSTOMISATION 2026-05-17 (v2.2):
    // Group cart line items by _product_key. Each group represents one
    // decoration configuration for one garment.
    //
    // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2) — MULTI-DECORATION:
    // A group can now carry MANY decorations (up to four methods + four
    // team slots). The grid previously kept only ONE decorationItem (the
    // first method) and ignored team slots entirely, so multi-decoration
    // garments showed only one decoration. We now collect ALL decorations
    // into an ordered array (g.decorations), then build:
    //   - aggKey:  colour + '|' + the ordered list of decoration variant IDs
    //              (so a unique full decoration-SET is its own grid row)
    //   - label:   every decoration title, in canonical order, comma-joined
    // Sample-only is detected via the garment's _sample_only flag (v2.2.4).
    // ==================================================================

    // BOOST CUSTOMISATION 2026-06-03 (v2.4.0): reordered to the flattened
    // picker rank (matches the cart/picker): embroidery, supacolour, heat_press
    // (Standard DTF), then the Names and Numbers team slots, then screen_print.
    var DECORATION_RANK = {
      embroidery: 1,
      supacolour: 2,
      heat_press: 3,
      team_name: 4,
      player_name: 5,
      front_number: 6,
      back_number: 7,
      screen_print: 8
    };

    var groupsByPk = {};
    /* ============================================================
       BOOST CUSTOMISATION 2026-05-18 (v2.2.3):
       PERF: merged previously two sequential forEach passes over
       cart.items into one. Pass 1 was decorated-group classification;
       pass 2 was plain-qty accumulation for this product's
       non-_product_key lines. They operated on disjoint subsets of
       cart.items so the merge is safe and halves the iteration count
       for this hot function (runs on every boost:cart-updated event).
       ============================================================ */
    var plainQtyByVariantId = {};
    var plainLineIndicesByVariantId = {};
    // BOOST CUSTOMISATION 2026-05-23 (KEY-ADDRESSED REWRITE): map each plain
    // variant id to its cart line `key`(s), so a plain qty edit is a single
    // keyed /cart/change.js rather than a positional update.
    var plainKeysByVariantId = {};
    items.forEach(function (item, idx) {
      var props = item.properties || {};
      var pk = props._product_key || '';

      // Branch A — item belongs to a _product_key group with this
      // product's prefix: classify it into the group.
      if (pk && pk.indexOf(self.productKeyPrefix) === 0) {
        if (!groupsByPk[pk]) {
          groupsByPk[pk] = {
            garmentItem: null,
            garmentIdx: -1,
            carriers: [],
            decorationTracker: null,
            decorations: []   // BOOST v2.3.0 Stage 2: ALL decorations (methods + team)
          };
        }
        var g = groupsByPk[pk];
        var isTracker = (props._hidden_in_cart === 'true' || props._hidden_in_cart === true);
        var isGarmentOfThisProduct = (Number(item.product_id) === self.productId)
                                  && !isTracker
                                  && !props._product_option;
        if (isGarmentOfThisProduct) {
          g.garmentItem = item;
          g.garmentIdx = idx;
        } else if (isTracker) {
          g.decorationTracker = item;
          g.carriers.push({ item: item, idx: idx });
        } else {
          // Carrier — decoration (method OR team slot) or sample-confirmation.
          // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2): collect EVERY
          // decoration (methods AND team slots), not just the first method.
          var po = props._product_option || '';
          var dm = props._decoration_method || '';
          if (po === 'decoration') {
            g.decorations.push({
              method: dm,
              rank: (typeof DECORATION_RANK[dm] === 'number') ? DECORATION_RANK[dm] : 99,
              variantId: item.variant_id,
              title: item.product_title || 'Decoration',
              // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2): capture the
              // decoration's PDP url so the grid can link each decoration line
              // (mirrors the cart drawer / cart page, which link via line_item.url).
              url: item.url || ''
            });
          }
          g.carriers.push({ item: item, idx: idx });
        }
        return;
      }

      // Branch B — plain (non-decorated) line of this product.
      if (Number(item.product_id) === self.productId && !pk) {
        plainQtyByVariantId[item.variant_id] = (plainQtyByVariantId[item.variant_id] || 0) + item.quantity;
        if (!plainLineIndicesByVariantId[item.variant_id]) plainLineIndicesByVariantId[item.variant_id] = [];
        plainLineIndicesByVariantId[item.variant_id].push(idx);
        // BOOST CUSTOMISATION 2026-05-23 (KEY-ADDRESSED REWRITE): capture key.
        if (item.key) {
          if (!plainKeysByVariantId[item.variant_id]) plainKeysByVariantId[item.variant_id] = [];
          plainKeysByVariantId[item.variant_id].push(item.key);
        }
      }
    });

    var rowsByAggKey = {};
    Object.keys(groupsByPk).forEach(function (pk) {
      var g = groupsByPk[pk];
      if (!g.garmentItem) return;
      var colour = '';
      var size = '';
      var ovs = g.garmentItem.options_with_values || [];
      ovs.forEach(function (ov) {
        if (ov.name === 'Colour' || ov.name === 'Color') colour = ov.value;
        else if (ov.name === 'Size') size = ov.value;
      });
      if ((!colour || !size) && g.garmentItem.variant_options) {
        if (!colour) colour = g.garmentItem.variant_options[self.colourIdx] || '';
        if (!size) size = g.garmentItem.variant_options[self.sizeIdx] || '';
      }
      if (!colour) return;

      /* ============================================================
         BOOST CUSTOMISATION 2026-05-18 (v2.2.4):
         Sample-only detection reads _sample_only from the GARMENT line.
         The legacy v2.2 tracker is checked as a fallback for carts
         created before v2.2.4 shipped (which still have the tracker
         line present).
         The v2.2 rewrite already moved decorationLabel away from the
         tracker's _decoration_type / _decoration_pitch (those were the
         source of the v2.1 "(unspecified)" rendering bug). v2.2.3 then
         fixed the .product.title crash. v2.2.4 completes the tracker
         retirement by reading the sample flag from the garment instead.
         ============================================================ */
      var garmentProps = g.garmentItem.properties || {};
      var garmentSampleFlag = garmentProps._sample_only;
      var legacyTrackerProps = g.decorationTracker ? (g.decorationTracker.properties || {}) : {};
      var legacyTrackerSampleFlag = legacyTrackerProps._sample_only;
      var isSampleOnly = (garmentSampleFlag === 'true' || garmentSampleFlag === true)
                      || (legacyTrackerSampleFlag === 'true' || legacyTrackerSampleFlag === true);

      var aggKey;
      var decorationLabel;
      var decorationDisplay = null; // BOOST v2.3.0 Stage 2: [{title, url}, ...] in canonical order
      if (isSampleOnly) {
        aggKey = colour + '|sample';
        decorationLabel = 'Sample only';
      } else if (g.decorations && g.decorations.length > 0) {
        /* ============================================================
           BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2) — MULTI-DECORATION:
           Build the aggregation key and label from the FULL ordered set of
           decorations on this group, so a unique decoration-set is its own
           grid row, and the label lists every decoration in canonical order.
           Sort a COPY by rank (then variantId as a tiebreak) so ordering is
           deterministic regardless of cart line order.
           ============================================================ */
        var orderedDecos = g.decorations.slice().sort(function (a, b) {
          if (a.rank !== b.rank) return a.rank - b.rank;
          return Number(a.variantId) - Number(b.variantId);
        });
        // aggKey: colour + each decoration variant id in order. Two garments
        // share a row only if colour AND the exact decoration-set match.
        var keyParts = orderedDecos.map(function (d) { return d.variantId; });
        aggKey = colour + '|' + keyParts.join('+');
        // Label: every decoration title, canonical order, comma-joined.
        // Retained for sorting and the sticky-row snapshot. product_title
        // fallback to 'Decoration' (Shopify Ajax line items are flat — no
        // nested .product object; v2.2.3 lesson).
        decorationLabel = orderedDecos.map(function (d) {
          return d.title || 'Decoration';
        }).join(', ');
        // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2): structured display
        // list — each decoration's title + PDP url, in canonical order. The
        // row renders this as the "Included on this item:" heading followed
        // by one linked line per decoration (matching the cart drawer/page).
        decorationDisplay = orderedDecos.map(function (d) {
          return { title: d.title || 'Decoration', url: d.url || '' };
        });
      } else {
        // Decorated group with no decoration lines — shouldn't happen, but
        // fall back gracefully rather than crashing.
        aggKey = colour + '|unknown-' + pk;
        decorationLabel = '(unspecified)';
      }

      if (!rowsByAggKey[aggKey]) {
        rowsByAggKey[aggKey] = self.makeEmptyAggRow(aggKey, colour, decorationLabel);
      }
      var row = rowsByAggKey[aggKey];

      // BOOST CUSTOMISATION 2026-05-22 (v2.3.0 Stage 2): mark sample-only rows
      // and capture the sample-confirmation carrier (e.g. the AS Colour
      // acknowledgement) so a new size added from the grid can be re-created
      // as another sample-only line WITH the same acknowledgement carrier.
      if (isSampleOnly) {
        row.isSampleOnly = true;
        if (!row.sampleCarrierForClone) {
          g.carriers.forEach(function (c) {
            var cp = (c.item && c.item.properties) || {};
            if (cp._product_option === 'sample_confirmation') {
              row.sampleCarrierForClone = {
                variantId: c.item.variant_id,
                vendor: cp._sample_vendor || ''
              };
              // BOOST CUSTOMISATION 2026-05-22 (Fixes Round 2): capture the
              // acknowledgement title + url so the grid can render it as a
              // visible linked display line under the sample-only text
              // (matching the cart drawer / cart page). Ajax line items are
              // flat — title is product_title, url is line_item.url.
              if (!row.sampleAckDisplay) {
                row.sampleAckDisplay = {
                  title: c.item.product_title || '',
                  url: c.item.url || ''
                };
              }
            }
          });
        }
      }

      // Attach the structured display list once per row (first defining group).
      if (!row.decorationDisplay && decorationDisplay) {
        row.decorationDisplay = decorationDisplay;
      }

      // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2): record the rank-sorted
      // decoration set (variant id + method) once per row, used to clone the
      // decorations when a NEW size is added via the grid. Set from the first
      // group that defines the row (all groups in a row share the same set by
      // definition of the aggKey).
      if (!row.decorationsForClone && g.decorations && g.decorations.length > 0) {
        row.decorationsForClone = g.decorations.slice().sort(function (a, b) {
          if (a.rank !== b.rank) return a.rank - b.rank;
          return Number(a.variantId) - Number(b.variantId);
        }).map(function (d) {
          return { variantId: d.variantId, method: d.method };
        });
      }

      if (size) {
        row.qtyBySize[size] = (row.qtyBySize[size] || 0) + g.garmentItem.quantity;
        if (!row.lineItemIndicesByGarmentSize[size]) row.lineItemIndicesByGarmentSize[size] = [];
        row.lineItemIndicesByGarmentSize[size].push(g.garmentIdx);

        // ============================================================
        // BOOST CUSTOMISATION 2026-05-23 (KEY-ADDRESSED REWRITE):
        // Capture the backing pk GROUP for this size, addressed by the
        // immutable line `key` of each line (NOT positional index). Under
        // option 3 (one pk group per unique colour+size+decoration-set) each
        // size resolves to exactly one group, but we store an array to stay
        // correct if a duplicate group ever exists (e.g. a picker-created
        // group of the same combination): an edit then targets ALL backing
        // groups' lines by key, which is still safe because keys are unique.
        //
        // Keys disambiguate carriers that share a variant id (the exact bug
        // that defeated the positional model: two embroidery carriers, same
        // variant 53306425377067, different keys). /cart/change.js targets a
        // single line by key, so editing one size never touches another.
        // ============================================================
        if (!row.groupsBySize[size]) row.groupsBySize[size] = [];
        var carrierKeysForGroup = [];
        g.carriers.forEach(function (c) {
          if (c.item && c.item.key) {
            carrierKeysForGroup.push({
              key: c.item.key,
              variantId: c.item.variant_id,
              // sample-confirmation carriers stay at qty 1 regardless of
              // garment qty; decoration carriers track garment qty.
              isSampleConfirmation: ((c.item.properties || {})._product_option === 'sample_confirmation')
            });
          }
        });
        row.groupsBySize[size].push({
          pk: pk,
          garmentKey: g.garmentItem.key,
          garmentVariantId: g.garmentItem.variant_id,
          carrierKeys: carrierKeysForGroup
        });
      }

      g.carriers.forEach(function (c) {
        row.carrierLineItems.push({ idx: c.idx, item: c.item, productKey: pk });
      });

      row.garmentVariantPrice = g.garmentItem.price;

      var thisGroupCarrierUnit = 0;
      g.carriers.forEach(function (c) {
        if (c.item.quantity > 0) {
          thisGroupCarrierUnit += Math.round(Number(c.item.line_price || c.item.price * c.item.quantity) / c.item.quantity);
        }
      });
      if (thisGroupCarrierUnit > row.carrierUnitPricePerGarment) {
        row.carrierUnitPricePerGarment = thisGroupCarrierUnit;
      }

      if (!row.thumbSrc && g.garmentItem.featured_image && g.garmentItem.featured_image.url) {
        row.thumbSrc = g.garmentItem.featured_image.url;
      }
      // BOOST CUSTOMISATION 2026-05-22 (Fixes Round 2): store the garment
      // product title on the row so it can be written as the visible
      // "For garment" property on decoration carriers (see Section 4.5 of
      // the handoff). Shopify Ajax line items expose product_title as a flat
      // string (not a nested .product object — v2.2.3 lesson).
      if (!row.garmentProductTitle && g.garmentItem.product_title) {
        row.garmentProductTitle = g.garmentItem.product_title;
      }
    });

    Object.keys(rowsByAggKey).forEach(function (aggKey) {
      var row = rowsByAggKey[aggKey];
      var plainRow = null;
      var plainRows = self.plainTbody.querySelectorAll('[data-boost-bulk-plain-row]');
      for (var i = 0; i < plainRows.length; i++) {
        if (plainRows[i].dataset.colour === row.colour) { plainRow = plainRows[i]; break; }
      }
      if (!plainRow) return;
      self.sizeValues.forEach(function (sizeValue) {
        var plainInputs = plainRow.querySelectorAll('[data-boost-bulk-input]');
        for (var k = 0; k < plainInputs.length; k++) {
          if (plainInputs[k].getAttribute('aria-label') === row.colour + ' ' + sizeValue) {
            row.garmentVariantIdBySize[sizeValue] = Number(plainInputs[k].dataset.variantId);
            break;
          }
        }
      });
      var plainThumb = plainRow.querySelector('.boost-bulk-grid-thumb');
      if (plainThumb) row.thumbSrc = plainThumb.getAttribute('src') || row.thumbSrc;
    });

    Object.keys(this.stickyAggKeys).forEach(function (aggKey) {
      if (rowsByAggKey[aggKey]) return;
      var snap = self.stickyRowSnapshots[aggKey];
      if (!snap) return;
      var stickyRow = self.makeEmptyAggRow(aggKey, snap.colour, snap.decorationLabel);
      stickyRow.thumbSrc = snap.thumbSrc;
      stickyRow.garmentVariantPrice = snap.garmentVariantPrice;
      stickyRow.carrierUnitPricePerGarment = snap.carrierUnitPricePerGarment;
      // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2): restore decoration set
      // so adding a size to a zeroed (sticky) row recreates the decorations.
      stickyRow.decorationsForClone = snap.decorationsForClone || null;
      stickyRow.decorationDisplay = snap.decorationDisplay || null;
      stickyRow.isSampleOnly = snap.isSampleOnly || false;
      stickyRow.sampleCarrierForClone = snap.sampleCarrierForClone || null;
      stickyRow.garmentProductTitle = snap.garmentProductTitle || '';
      stickyRow.sampleAckDisplay = snap.sampleAckDisplay || null;
      var plainRows = self.plainTbody.querySelectorAll('[data-boost-bulk-plain-row]');
      for (var i = 0; i < plainRows.length; i++) {
        if (plainRows[i].dataset.colour === snap.colour) {
          self.sizeValues.forEach(function (sizeValue) {
            var pIns = plainRows[i].querySelectorAll('[data-boost-bulk-input]');
            for (var k = 0; k < pIns.length; k++) {
              if (pIns[k].getAttribute('aria-label') === snap.colour + ' ' + sizeValue) {
                stickyRow.garmentVariantIdBySize[sizeValue] = Number(pIns[k].dataset.variantId);
                break;
              }
            }
          });
          break;
        }
      }
      rowsByAggKey[aggKey] = stickyRow;
    });

    this.decoratedRowsByAggKey = rowsByAggKey;
    this.plainQtyByVariantId = plainQtyByVariantId;
    this.plainLineIndicesByVariantId = plainLineIndicesByVariantId;
    this.plainKeysByVariantId = plainKeysByVariantId;
  };

  BoostBulkGrid.prototype.makeEmptyAggRow = function (aggKey, colour, decorationLabel) {
    return {
      aggKey: aggKey,
      colour: colour,
      decorationLabel: decorationLabel,
      // BOOST CUSTOMISATION 2026-05-17 (v2.2):
      // Label prefix changed from "Artwork:" to "Decoration:" to match
      // the corrected terminology. Artwork = the design/file prep service
      // (Artwork Setup Fee). Decoration = the method + specific item
      // applied to a garment (e.g. "Embroidery - 1 Standard Logo").
      // BOOST CUSTOMISATION 2026-05-22: sample-only rows use the cart's exact
      // phrase "Sample only. No decoration applied." (matches side-cart /
      // main-cart) instead of "Decoration: Sample only".
      artworkLabelDisplay: (decorationLabel === 'Sample only')
        ? 'Sample only. No decoration applied.'
        : ('Decoration: ' + decorationLabel),
      thumbSrc: '',
      garmentVariantPrice: 0,
      carrierUnitPricePerGarment: 0,
      garmentVariantIdBySize: {},
      qtyBySize: {},
      lineItemIndicesByGarmentSize: {},
      carrierLineItems: [],
      // BOOST CUSTOMISATION 2026-05-23 (KEY-ADDRESSED REWRITE): per-size backing
      // pk group(s), each with the garment line `key` and its carrier line
      // `key`s. Replaces positional index math for edit/remove. Shape:
      //   { 'M': [ { pk, garmentKey, garmentVariantId,
      //             carrierKeys: [ { key, variantId, isSampleConfirmation } ] } ] }
      groupsBySize: {},
      // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2): rank-sorted decoration
      // set (variant id + method) for cloning to a newly-added size. Populated
      // from the first group that defines the row, or from a sticky snapshot.
      decorationsForClone: null,
      // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2): [{title, url}, ...] in
      // canonical order, rendered as the "Included on this item:" heading plus
      // one linked line per decoration (matches the cart drawer / cart page).
      // BOOST CUSTOMISATION 2026-05-22 (v2.3.0 Stage 2): sample-only row flag
      // + the sample-confirmation carrier to clone when adding a new size.
      isSampleOnly: false,
      sampleCarrierForClone: null,
      decorationDisplay: null,
      // BOOST CUSTOMISATION 2026-05-22 (Fixes Round 2): garment product title
      // for the "For garment" visible property on carrier lines.
      garmentProductTitle: '',
      // BOOST CUSTOMISATION 2026-05-22 (Fixes Round 2): {title, url} of the
      // acknowledgement carrier, rendered as a linked line under the sample
      // note in the grid (matches cart drawer / cart page).
      sampleAckDisplay: null
    };
  };

  BoostBulkGrid.prototype.renderDecoratedRows = function () {
    var self = this;
    var existing = this.decoratedTbody.querySelectorAll('[data-boost-bulk-decorated-row]');
    existing.forEach(function (r) { r.remove(); });

    var aggKeys = Object.keys(this.decoratedRowsByAggKey);
    if (!aggKeys.length) {
      this.decoratedTbody.setAttribute('hidden', '');
      return;
    }
    this.decoratedTbody.removeAttribute('hidden');

    aggKeys.sort(function (a, b) {
      var ra = self.decoratedRowsByAggKey[a];
      var rb = self.decoratedRowsByAggKey[b];
      if (ra.colour < rb.colour) return -1;
      if (ra.colour > rb.colour) return 1;
      if (ra.decorationLabel < rb.decorationLabel) return -1;
      if (ra.decorationLabel > rb.decorationLabel) return 1;
      return 0;
    });
    aggKeys.forEach(function (aggKey) {
      var rowEl = self.buildDecoratedRowElement(self.decoratedRowsByAggKey[aggKey]);
      self.decoratedTbody.appendChild(rowEl);
    });
  };

  BoostBulkGrid.prototype.buildDecoratedRowElement = function (row) {
    var self = this;
    var fragment = this.decoratedRowTemplate.content.cloneNode(true);
    var rowEl = fragment.querySelector('[data-boost-bulk-decorated-row]');
    rowEl.dataset.aggKey = row.aggKey;

    var thumb = rowEl.querySelector('.boost-bulk-grid-thumb');
    if (thumb && row.thumbSrc) {
      thumb.setAttribute('src', row.thumbSrc);
      thumb.setAttribute('alt', row.colour);
    } else if (thumb) {
      thumb.style.visibility = 'hidden';
    }

    var colourEl = rowEl.querySelector('[data-boost-bulk-colour-name]');
    if (colourEl) colourEl.textContent = row.colour;
    var labelEl = rowEl.querySelector('[data-boost-bulk-artwork-label]');
    if (labelEl) {
      // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2):
      // Render the decoration label as the "Included on this item:" heading
      // (plain) followed by one linked line per decoration, in canonical
      // order — identical to the cart drawer and cart page. Built as DOM
      // nodes (not innerHTML) so titles containing parentheses/ampersands are
      // safe. Falls back to the plain text label for sample-only / legacy
      // rows that have no structured decorationDisplay.
      labelEl.textContent = '';
      if (row.decorationDisplay && row.decorationDisplay.length > 0) {
        var heading = document.createElement('p');
        heading.className = 'boost-decoration-heading';
        heading.textContent = 'Included on this item:';
        labelEl.appendChild(heading);

        var ul = document.createElement('ul');
        ul.className = 'boost-decoration-list';
        row.decorationDisplay.forEach(function (d) {
          var li = document.createElement('li');
          li.className = 'boost-decoration-line';
          if (d.url) {
            var a = document.createElement('a');
            a.setAttribute('href', d.url);
            a.textContent = d.title;
            li.appendChild(a);
          } else {
            li.textContent = d.title;
          }
          ul.appendChild(li);
        });
        labelEl.appendChild(ul);
      } else {
        // Sample-only or legacy fallback. Render as DOM nodes (not plain
        // textContent) so the acknowledgement can be appended as a linked line.
        // BOOST CUSTOMISATION 2026-05-22 (Fixes Round 2): show the
        // acknowledgement (AS Colour or generic) as a linked display line
        // below the "Sample only. No decoration applied." note, matching the
        // cart drawer / cart page.
        var noteP = document.createElement('p');
        noteP.className = 'boost-decoration-note';
        noteP.textContent = row.artworkLabelDisplay;
        labelEl.appendChild(noteP);

        if (row.isSampleOnly && row.sampleAckDisplay && row.sampleAckDisplay.title) {
          var ackP = document.createElement('p');
          ackP.className = 'boost-decoration-note boost-sample-ack-line';
          if (row.sampleAckDisplay.url) {
            var ackA = document.createElement('a');
            ackA.setAttribute('href', row.sampleAckDisplay.url);
            ackA.textContent = row.sampleAckDisplay.title;
            ackP.appendChild(ackA);
          } else {
            ackP.textContent = row.sampleAckDisplay.title;
          }
          labelEl.appendChild(ackP);
        }
      }
    }

    var removeCell = rowEl.querySelector('.boost-bulk-grid-remove-cell');
    this.sizeValues.forEach(function (sizeValue) {
      var variantId = row.garmentVariantIdBySize[sizeValue];
      var td;
      if (variantId) {
        td = document.createElement('td');
        td.className = 'boost-bulk-grid-cell';
        var input = document.createElement('input');
        input.type = 'number';
        input.min = '0';
        input.step = '1';
        input.setAttribute('inputmode', 'numeric');
        input.setAttribute('data-boost-bulk-input', '');
        input.setAttribute('data-boost-bulk-row-type', 'decorated');
        input.setAttribute('data-variant-id', String(variantId));
        input.setAttribute('data-variant-price', String(row.garmentVariantPrice));
        input.setAttribute('data-boost-bulk-agg-key', row.aggKey);
        input.setAttribute('data-boost-bulk-size', sizeValue);
        input.setAttribute('aria-label', row.colour + ' ' + sizeValue + ' decorated');
        var qty = row.qtyBySize[sizeValue] || 0;
        if (qty > 0) input.value = String(qty);
        td.appendChild(input);
        self.attachCellListeners(input);
      } else {
        td = document.createElement('td');
        td.className = 'boost-bulk-grid-cell boost-bulk-grid-cell-disabled';
        td.setAttribute('aria-label', 'Not available');
        td.setAttribute('title', 'Not available');
      }
      rowEl.insertBefore(td, removeCell);
    });

    var removeX = rowEl.querySelector('[data-boost-bulk-remove-x]');
    if (removeX) {
      removeX.addEventListener('click', function () { self.handleRemoveDecoratedRow(row.aggKey); });
      removeX.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.code === 'Space') {
          e.preventDefault();
          self.handleRemoveDecoratedRow(row.aggKey);
        }
      });
    }
    return rowEl;
  };

  BoostBulkGrid.prototype.syncPlainInputsFromState = function () {
    var self = this;
    this.plainInputs.forEach(function (inp) {
      if (document.activeElement === inp) return;
      var vid = Number(inp.dataset.variantId);
      var qty = self.plainQtyByVariantId[vid] || 0;
      inp.value = qty > 0 ? String(qty) : '';
    });
  };

  BoostBulkGrid.prototype.recalcTotals = function () {
    var x = 0, y = 0, priceX = 0, priceY = 0;
    var self = this;
    this.plainInputs.forEach(function (inp) {
      var q = parseInt(inp.value, 10);
      if (isNaN(q) || q <= 0) return;
      x += q;
      priceX += q * Number(inp.dataset.variantPrice || 0);
    });
    var decRows = this.decoratedTbody.querySelectorAll('[data-boost-bulk-decorated-row]');
    decRows.forEach(function (rowEl) {
      var aggKey = rowEl.dataset.aggKey;
      var row = self.decoratedRowsByAggKey[aggKey];
      if (!row) return;
      var inputs = rowEl.querySelectorAll('[data-boost-bulk-input]');
      var rowQty = 0, rowPrice = 0;
      inputs.forEach(function (inp) {
        var q = parseInt(inp.value, 10);
        if (isNaN(q) || q <= 0) return;
        rowQty += q;
        rowPrice += q * row.garmentVariantPrice;
      });
      rowPrice += rowQty * row.carrierUnitPricePerGarment;
      x += rowQty;
      y += rowQty;
      priceX += rowPrice;
      priceY += rowPrice;
    });
    this.renderTotals(x, y, priceX, priceY);
  };

  BoostBulkGrid.prototype.renderTotals = function (x, y, priceX, priceY) {
    var hasPlain = (x - y) > 0;
    var hasDecorated = y > 0;
    var unitsText, priceText;
    if (!hasDecorated && !hasPlain) {
      unitsText = '0';
      priceText = formatMoney(0, this.currency);
    } else if (hasDecorated && hasPlain) {
      unitsText = x + ' (' + y + ' decorated)';
      priceText = formatMoney(priceX, this.currency) + ' (' + formatMoney(priceY, this.currency) + ' for decorated items)';
    } else if (hasDecorated && !hasPlain) {
      unitsText = String(y);
      priceText = formatMoney(priceY, this.currency);
    } else {
      unitsText = String(x);
      priceText = formatMoney(priceX, this.currency);
    }
    this.unitsValueEl.textContent = unitsText;
    this.priceValueEl.textContent = priceText;
  };

  BoostBulkGrid.prototype.updateRemoveButtonVisibility = function () {
    var rows = this.decoratedTbody.querySelectorAll('[data-boost-bulk-decorated-row]');
    rows.forEach(function (rowEl) {
      var inputs = rowEl.querySelectorAll('[data-boost-bulk-input]');
      var sum = 0;
      inputs.forEach(function (inp) {
        var q = parseInt(inp.value, 10);
        if (!isNaN(q) && q > 0) sum += q;
      });
      var removeX = rowEl.querySelector('[data-boost-bulk-remove-x]');
      if (!removeX) return;
      if (sum === 0) removeX.removeAttribute('hidden');
      else removeX.setAttribute('hidden', '');
    });
  };

  BoostBulkGrid.prototype.handleRemoveDecoratedRow = function (aggKey) {
    var row = this.decoratedRowsByAggKey[aggKey];
    if (!row) return;
    delete this.stickyAggKeys[aggKey];
    delete this.stickyRowSnapshots[aggKey];

    // ============================================================
    // BOOST CUSTOMISATION 2026-05-23 (KEY-ADDRESSED REWRITE):
    // Remove the row by zeroing every backing pk group's lines BY KEY. A row
    // can aggregate several sizes; each size has its own backing group(s).
    // We collect every garment + carrier line key across all sizes and set
    // them to 0 in a single keyed /cart/update.js. No positional array, so
    // unrelated lines (other rows' carriers, fees) are never disturbed. The
    // fee-manager then releases the fees once no decorated groups remain.
    // ============================================================
    var keyUpdates = {};
    Object.keys(row.groupsBySize).forEach(function (size) {
      (row.groupsBySize[size] || []).forEach(function (grp) {
        if (grp.garmentKey) keyUpdates[grp.garmentKey] = 0;
        (grp.carrierKeys || []).forEach(function (ck) {
          if (ck.key) keyUpdates[ck.key] = 0;
        });
      });
    });

    if (Object.keys(keyUpdates).length === 0) {
      // Nothing in the cart for this row (e.g. a sticky zeroed row). Just drop
      // it from the UI.
      var rowEl = null;
      var rs = this.decoratedTbody.querySelectorAll('[data-boost-bulk-decorated-row]');
      for (var i = 0; i < rs.length; i++) {
        if (rs[i].dataset.aggKey === aggKey) { rowEl = rs[i]; break; }
      }
      if (rowEl) rowEl.remove();
      delete this.decoratedRowsByAggKey[aggKey];
      if (!Object.keys(this.decoratedRowsByAggKey).length) {
        this.decoratedTbody.setAttribute('hidden', '');
      }
      this.recalcTotals();
      return;
    }

    var self = this;
    // BOOST CUSTOMISATION 2026-05-23 (STATE/RENDER SPLIT): claim the cycle
    // guard across the remove + state rebuild, mirroring the flush, so a
    // queued edit waits for fresh keys after a row removal.
    this._enterSubmission();
    Promise.resolve()
      .then(function () {
        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates: keyUpdates, sections: 'side-cart' })
        }).then(function (r) {
          if (!r.ok) {
            return r.text().then(function (t) {
              throw new Error('cart/update.js (remove) failed: ' + r.status + ' ' + t);
            });
          }
          return r.json();
        }).then(function (response) {
          self.applyDrawerSection(response, true);
          try { window.dispatchEvent(new CustomEvent('themeCartOpened')); } catch (e) {}
          document.dispatchEvent(new CustomEvent('boost:cart-updated', { detail: { source: 'bulk-grid-remove' } }));
        });
      }).then(function () {
        return self.refreshStateOnly();
      }).then(function () {
        self._exitSubmission();
        self.refreshFromCart();
      }).catch(function (err) {
        self._exitSubmission();
        console.error('[boost-bulk-grid] remove failed:', err);
      });
  };

  BoostBulkGrid.prototype.queueCellUpdate = function (inp) {
    var self = this;
    var rowType = inp.dataset.boostBulkRowType;
    var qty = parseInt(inp.value, 10);
    if (isNaN(qty) || qty < 0) qty = 0;

    if (rowType === 'plain') {
      // BOOST CUSTOMISATION 2026-05-23 (KEY-ADDRESSED REWRITE): plain rows now
      // resolve their backing cart line(s) by `key` too, so a plain qty edit
      // is a single keyed change and never relies on positional indices. A
      // plain variant with no cart line yet routes to a plain add.
      var variantId = Number(inp.dataset.variantId);
      var plainKeys = this.plainKeysByVariantId[variantId] || [];
      if (plainKeys.length === 0) {
        if (qty > 0) this.pendingPlainAdds[variantId] = qty;
        else delete this.pendingPlainAdds[variantId];
      } else {
        // First key takes the qty; any duplicates collapse to 0 (Shopify
        // merges identical plain lines, so duplicates are rare, but stay safe).
        plainKeys.forEach(function (k, i) {
          self.pendingKeyChanges[k] = (i === 0) ? qty : 0;
        });
        delete this.pendingPlainAdds[variantId];
      }
    } else if (rowType === 'decorated') {
      var aggKey = inp.dataset.boostBulkAggKey;
      var size = inp.dataset.boostBulkSize;
      var row = this.decoratedRowsByAggKey[aggKey];
      if (!row) return;

      var addKey = aggKey + '|' + size;
      // BOOST CUSTOMISATION 2026-05-23 (KEY-ADDRESSED REWRITE): a size is
      // "existing" when it has at least one backing pk group (with line keys).
      // Otherwise it is a NEW size and routes to the add path. This replaces
      // the positional lineItemIndicesByGarmentSize check.
      var backingGroupsForSize = row.groupsBySize[size] || [];
      if (backingGroupsForSize.length === 0) {
        // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2): ADD A NEW SIZE to
        // a decorated row. This size has no backing cart line yet, so a
        // positional update can't target it. Instead we queue a fresh add:
        // a new garment line for this size PLUS a clone of every decoration
        // carrier on the row, all under a new _product_key (each garment-size
        // config is its own group in the cart, shown as its own row in the
        // drawer/cart page). qty 0 simply clears any pending add for this cell.
        var newGarmentVariantId = row.garmentVariantIdBySize[size];
        if (qty > 0 && newGarmentVariantId) {
          // BOOST CUSTOMISATION 2026-05-22 (v2.3.0 Stage 2): SAMPLE-ONLY rows
          // have no decorations to clone. Route them to a sample-only add
          // (garment with _sample_only + the sample-confirmation carrier, if
          // any) rather than the decorated-add path — which previously built
          // an empty decoration set, producing the "(unspecified)" row and a
          // /cart/update.js 422.
          if (row.isSampleOnly) {
            self.pendingSampleAdds[addKey] = {
              garmentVariantId: Number(newGarmentVariantId),
              quantity: qty,
              sampleCarrier: row.sampleCarrierForClone || null,
              // BOOST CUSTOMISATION 2026-05-22 (Fixes Round 2): garment
              // reference for the visible "For garment" property.
              garmentLabel: (row.garmentProductTitle || '') + ' (' + row.colour + ' / ' + size + ')'
            };
            delete self.pendingDecoratedAdds[addKey];
          } else {
            // Decorated row: clone the decoration carrier set (canonical order).
            var seenCarrier = {};
            var carrierSet = [];
            (row.decorationsForClone || []).forEach(function (d) {
              if (seenCarrier[d.variantId]) return;
              seenCarrier[d.variantId] = true;
              carrierSet.push({ variantId: d.variantId, method: d.method });
            });
            self.pendingDecoratedAdds[addKey] = {
              garmentVariantId: Number(newGarmentVariantId),
              quantity: qty,
              carriers: carrierSet,
              // BOOST CUSTOMISATION 2026-05-22 (Fixes Round 2): garment
              // reference for the visible "For garment" property.
              garmentLabel: (row.garmentProductTitle || '') + ' (' + row.colour + ' / ' + size + ')'
            };
            delete self.pendingSampleAdds[addKey];
          }
        } else {
          delete self.pendingDecoratedAdds[addKey];
          delete self.pendingSampleAdds[addKey];
        }
      } else {
        // ============================================================
        // BOOST CUSTOMISATION 2026-05-23 (KEY-ADDRESSED REWRITE):
        // EDIT AN EXISTING SIZE. Operate ONLY on the backing pk group(s) for
        // this exact size, addressing each line by its immutable `key`. This
        // replaces the old positional whole-row arithmetic that:
        //   (a) summed quantity across the whole aggregated row and forced
        //       that total onto one carrier (corrupting other sizes that
        //       share the same carrier variant id), and
        //   (b) built a stale positional /cart/update.js array that zeroed
        //       unrelated lines (other carriers, the $50 fee).
        //
        // Under option 3 a size has exactly one backing group, but we handle
        // an array defensively (a picker-created duplicate of the same
        // combination). For each backing group:
        //   - set the garment line (by key) to qty
        //   - set each decoration carrier (by key) to qty
        //   - leave sample-confirmation carriers at 1 (they never scale)
        // Every other size's group is untouched because we only reference
        // THIS size's keys.
        // ============================================================
        delete self.pendingDecoratedAdds[addKey];
        delete self.pendingSampleAdds[addKey];

        var backingGroups = row.groupsBySize[size] || [];
        backingGroups.forEach(function (grp) {
          // Garment line → qty (0 removes it).
          if (grp.garmentKey) {
            self.pendingKeyChanges[grp.garmentKey] = qty;
          }
          // Carrier lines: decoration carriers track garment qty; a sample
          // confirmation carrier stays at 1 while the garment exists, and
          // goes to 0 when the garment is removed.
          (grp.carrierKeys || []).forEach(function (ck) {
            if (!ck.key) return;
            if (ck.isSampleConfirmation) {
              self.pendingKeyChanges[ck.key] = (qty > 0) ? 1 : 0;
            } else {
              self.pendingKeyChanges[ck.key] = qty;
            }
          });
        });
      }
    }

    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(function () { self.flushPendingUpdates(); }, DEBOUNCE_MS);
  };

  BoostBulkGrid.prototype.flushPendingUpdates = function () {
    // BOOST CUSTOMISATION 2026-05-22 (Fixes Round 2): clear debounceTimer to
    // null BEFORE doing any work so that refreshFromCart (called from the
    // patchFetchAndXhr intercept during the flush) sees debounceTimer===null
    // and does not skip early. Without this, a cart mutation mid-flush leaves
    // debounceTimer non-null (pointing to a timer that already fired), causing
    // the post-flush refreshFromCart to be gated out and the grid to go stale.
    this.debounceTimer = null;

    if (this.submissionInFlight) {
      var s = this;
      this.debounceTimer = setTimeout(function () { s.flushPendingUpdates(); }, DEBOUNCE_MS);
      return;
    }
    var positional = this.pendingPositionalUpdates;
    var keyChanges = this.pendingKeyChanges;
    var adds = this.pendingPlainAdds;
    var decoAdds = this.pendingDecoratedAdds;
    var sampleAdds = this.pendingSampleAdds;
    var hasPositional = Object.keys(positional).length > 0;
    var hasKeyChanges = Object.keys(keyChanges).length > 0;
    var hasAdds = Object.keys(adds).length > 0;
    var hasDecoAdds = Object.keys(decoAdds).length > 0;
    var hasSampleAdds = Object.keys(sampleAdds).length > 0;
    if (!hasPositional && !hasKeyChanges && !hasAdds && !hasDecoAdds && !hasSampleAdds) return;

    // BOOST CUSTOMISATION 2026-05-23 (STATE/RENDER SPLIT): claim the in-flight
    // guard synchronously, for the WHOLE cycle (server call + state rebuild),
    // before any await. flushPendingUpdates re-defers while in-flight, so a
    // settle timer that fires mid-cycle cannot start a second flush that reads
    // stale keys. Released once in the final completion below. Uses the depth
    // counter so the inner per-fetch submissionGuard calls compose with this
    // outer claim instead of prematurely releasing it.
    this._enterSubmission();

    this.pendingPositionalUpdates = {};
    this.pendingKeyChanges = {};
    this.pendingPlainAdds = {};
    this.pendingDecoratedAdds = {};
    this.pendingSampleAdds = {};

    var self = this;
    Object.keys(this.decoratedRowsByAggKey).forEach(function (aggKey) {
      var rs = self.decoratedTbody.querySelectorAll('[data-boost-bulk-decorated-row]');
      var rowEl = null;
      for (var i = 0; i < rs.length; i++) {
        if (rs[i].dataset.aggKey === aggKey) { rowEl = rs[i]; break; }
      }
      if (!rowEl) return;
      var inputs = rowEl.querySelectorAll('[data-boost-bulk-input]');
      var sum = 0;
      inputs.forEach(function (inp) {
        var q = parseInt(inp.value, 10);
        if (!isNaN(q) && q > 0) sum += q;
      });
      var row = self.decoratedRowsByAggKey[aggKey];
      if (sum === 0) {
        self.stickyAggKeys[aggKey] = true;
        self.stickyRowSnapshots[aggKey] = {
          colour: row.colour,
          decorationLabel: row.decorationLabel,
          thumbSrc: row.thumbSrc,
          garmentVariantPrice: row.garmentVariantPrice,
          carrierUnitPricePerGarment: row.carrierUnitPricePerGarment,
          // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2): preserve the
          // decoration set so a zeroed (sticky) row can still add a size.
          decorationsForClone: row.decorationsForClone || null,
          // Preserve the linked display so the sticky row still renders the
          // "Included on this item:" heading and linked decoration lines.
          decorationDisplay: row.decorationDisplay || null,
          // Preserve sample-only state + carrier so a zeroed sample row can
          // still add a size as another sample-only line.
          isSampleOnly: row.isSampleOnly || false,
          sampleCarrierForClone: row.sampleCarrierForClone || null,
          // BOOST CUSTOMISATION 2026-05-22 (Fixes Round 2): preserve title.
          garmentProductTitle: row.garmentProductTitle || '',
          // BOOST CUSTOMISATION 2026-05-22 (Fixes Round 2): preserve ack line.
          sampleAckDisplay: row.sampleAckDisplay || null
        };
      } else {
        delete self.stickyAggKeys[aggKey];
        delete self.stickyRowSnapshots[aggKey];
      }
    });

    var promise = Promise.resolve(null);

    // BOOST CUSTOMISATION 2026-05-23 (KEY-ADDRESSED REWRITE):
    // Key-addressed quantity changes via /cart/update.js with an `updates`
    // OBJECT keyed by line `key` (NOT a positional array). Shopify applies
    // each change to exactly the line with that key and leaves every other
    // line untouched, so an edit can never zero an unrelated carrier or fee,
    // and carriers that share a variant id are disambiguated by key. This is
    // the core fix for the cart-corruption bug. Runs FIRST so subsequent adds
    // (if any in the same flush) layer on top of a settled cart.
    if (hasKeyChanges) {
      promise = promise.then(function () {
        return self.submissionGuard(function () {
          return fetch('/cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ updates: keyChanges, sections: 'side-cart' })
          }).then(function (r) {
            if (!r.ok) {
              return r.text().then(function (t) {
                throw new Error('cart/update.js (key) failed: ' + r.status + ' ' + t);
              });
            }
            return r.json();
          }).then(function (response) {
            self.applyDrawerSection(response, false);
            document.dispatchEvent(new CustomEvent('boost:cart-updated', { detail: { source: 'bulk-grid-key-change' } }));
          });
        });
      });
    }

    if (hasPositional) {
      var snapshot = self.cartItemsSnapshot;
      var arr = new Array(snapshot.length);
      for (var i = 0; i < snapshot.length; i++) {
        arr[i] = (positional[i] !== undefined) ? positional[i] : snapshot[i].quantity;
      }
      promise = promise.then(function () {
        return self.fireCartUpdate(arr, /* openDrawer= */ false, null);
      });
    }
    if (hasAdds) {
      var items = [];
      Object.keys(adds).forEach(function (vid) {
        items.push({ id: Number(vid), quantity: adds[vid] });
      });
      promise = promise.then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items, sections: 'side-cart' })
        }).then(function (r) { return r.json(); })
          .then(function (response) {
            self.applyDrawerSection(response, false);
            document.dispatchEvent(new CustomEvent('boost:cart-updated', { detail: { source: 'bulk-grid' } }));
          });
      });
    }

    // BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2): DECORATED ADDS.
    // Add new sizes to decorated rows. Each pending entry becomes a garment
    // line + a clone of every decoration carrier, under a NEW _product_key
    // (so the cart shows it as its own grouped row, like the picker does).
    // Setup fees are kept once-per-order: we read the cart first and include
    // the $70 (any decoration) / $50 (any embroidery) fee ONLY if absent,
    // mirroring the picker's doFreshAdd. The fee-manager remains the
    // remover/enforcer.
    if (hasDecoAdds) {
      promise = promise.then(function () {
        return self.performDecoratedAdds(decoAdds);
      });
    }

    // BOOST CUSTOMISATION 2026-05-22 (v2.3.0 Stage 2): sample-only adds.
    if (hasSampleAdds) {
      promise = promise.then(function () {
        return self.performSampleAdds(sampleAdds);
      });
    }

    // BOOST CUSTOMISATION 2026-05-23 (STATE/RENDER SPLIT): after the commit,
    // ALWAYS rebuild the internal key maps from the fresh cart (safe while a
    // cell is focused — no repaint), THEN attempt the visual render (which the
    // focus guard correctly defers while a cell is focused). This guarantees
    // the next edit knows the current line keys, so reducing a quantity is a
    // keyed set-to-value change, never an additive add.
    //
    // The cycle-wide in-flight claim (_enterSubmission at the top) is released
    // only here, AFTER the state rebuild, so a rapidly-queued next flush waits
    // until the key maps are fresh. Without this, an edit fired in the brief
    // window after the commit but before the state rebuild could read stale
    // keys and add instead of set.
    promise.then(function () { return self.refreshStateOnly(); })
      .then(function () {
        self._exitSubmission();
        self.refreshFromCart();
      })
      .catch(function (err) {
        self._exitSubmission();
        console.warn('[boost-bulk-grid] flush failed:', err);
      });
  };

  /* ============================================
  BOOST CUSTOMISATION 2026-05-22 (v2.3.0 Stage 2):
  performSampleAdds — adds new SAMPLE-ONLY sizes from the grid. Each entry
  becomes a garment line marked _sample_only plus a clone of the
  sample-confirmation carrier under a NEW _product_key. Now handles both:
    - AS Colour garments: use the AS Colour acknowledgement (cloned from
      the row's existing carrier, or from data-as-colour-sample-id fallback).
    - Non-AS-Colour garments: use the generic sample acknowledgement
      (data-generic-sample-id, resolved from handle
      'confirmation-of-sample-product-order' in boost-bulk-order-grid.liquid).
  Graceful fallback: if neither acknowledgement id resolves, the garment is
  still added as sample-only; no acknowledgement carrier is written, and the
  theme will not break.
  BOOST CUSTOMISATION 2026-05-22 (Fixes Round 2): writes the VISIBLE property
  "For garment: <product title (colour / size)>" on the acknowledgement
  carrier so the garment<->sample relationship is legible at the Shopify
  hosted checkout and in Order Printer Pro templates.
  ============================================ */
  BoostBulkGrid.prototype.performSampleAdds = function (sampleAdds) {
    var self = this;
    var items = [];
    // AS Colour fallback carrier id (handle-resolved in Liquid).
    var asColourFallbackId = 0;
    if (this.root && this.root.dataset && this.root.dataset.asColourSampleId) {
      var parsedAs = parseInt(this.root.dataset.asColourSampleId, 10);
      if (!isNaN(parsedAs) && parsedAs > 0) asColourFallbackId = parsedAs;
    }
    // Generic sample acknowledgement carrier id (handle-resolved in Liquid).
    var genericSampleId = 0;
    if (this.root && this.root.dataset && this.root.dataset.genericSampleId) {
      var parsedGen = parseInt(this.root.dataset.genericSampleId, 10);
      if (!isNaN(parsedGen) && parsedGen > 0) genericSampleId = parsedGen;
    }

    function newKey() {
      return (self.productKeyPrefix || 'pk-') + Math.random().toString(16).slice(2, 8);
    }

    Object.keys(sampleAdds).forEach(function (addKey) {
      var entry = sampleAdds[addKey];
      if (!entry || !entry.garmentVariantId || entry.quantity <= 0) return;
      var pk = newKey();
      var garmentLabel = entry.garmentLabel || '';

      // Determine the acknowledgement carrier to write.
      // Priority: (1) carrier already on the row (AS Colour or generic),
      // (2) AS Colour fallback (data-as-colour-sample-id),
      // (3) generic fallback (data-generic-sample-id),
      // (4) none (add garment only, no ack carrier).
      var carrierVariantId = 0;
      var carrierVendor = '';
      var isAsColour = false;

      if (entry.sampleCarrier && entry.sampleCarrier.variantId) {
        carrierVariantId = Number(entry.sampleCarrier.variantId);
        carrierVendor = entry.sampleCarrier.vendor || '';
        isAsColour = !!carrierVendor; // AS Colour ack carries _sample_vendor
      } else if (asColourFallbackId) {
        // AS Colour fallback — the product is an AS Colour SKU but no carrier
        // was captured (e.g. the first add on a fresh cart).
        carrierVariantId = asColourFallbackId;
        isAsColour = true;
      } else if (genericSampleId) {
        carrierVariantId = genericSampleId;
        isAsColour = false;
      }

      // Carrier first (qty 1, stays at 1 regardless of garment qty).
      // Garment LAST so it floats to the group top under Shopify LIFO.
      if (carrierVariantId) {
        var carrierProps = {
          _product_key: pk,
          _product_option: 'sample_confirmation'
        };
        if (isAsColour && carrierVendor) carrierProps._sample_vendor = carrierVendor;
        // BOOST CUSTOMISATION 2026-05-22 (Fixes Round 2): visible "For garment"
        // property so the hosted checkout and Order Printer Pro show the
        // garment<->sample link. VISIBLE (no underscore) so Shopify renders it
        // at checkout. The themed cart suppresses the raw property row (carriers
        // are filtered from the themed cart render entirely).
        if (garmentLabel) carrierProps['For garment'] = garmentLabel;
        items.push({ id: carrierVariantId, quantity: 1, properties: carrierProps });
      }
      items.push({
        id: entry.garmentVariantId,
        quantity: entry.quantity,
        properties: { _product_key: pk, _sample_only: 'true' }
      });
    });

    if (items.length === 0) return Promise.resolve(null);

    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items, sections: 'side-cart' })
    }).then(function (r) { return r.json(); })
      .then(function (response) {
        self.applyDrawerSection(response, false);
        document.dispatchEvent(new CustomEvent('boost:cart-updated', { detail: { source: 'bulk-grid-sample-add' } }));
      });
  };

  /* ============================================
  BOOST CUSTOMISATION 2026-05-21 (v2.3.0 Stage 2):
  performDecoratedAdds — adds new decorated sizes from the grid.
  For each pending entry: build [garment + cloned carriers] under a new
  _product_key. Reads the cart once to decide fee inclusion (once-per-order
  parity with the picker): $70 if any decoration present-or-being-added and
  not already in cart; $50 if any embroidery is being added and not already
  in cart. Fees are added as their own array elements; the cart's LIFO
  ordering then places the garment on top of its group.
  ============================================ */
  BoostBulkGrid.prototype.getFeeConfig = function () {
    var cfg = (typeof window !== 'undefined' && window.BoostFeeConfig) ? window.BoostFeeConfig : {};
    var artwork = Number(cfg.feeVariantId);
    var emb = Number(cfg.embroideryFeeVariantId);
    return {
      artworkFeeVariantId: (!isNaN(artwork) && artwork > 0) ? artwork : null,
      embroideryFeeVariantId: (!isNaN(emb) && emb > 0) ? emb : null
    };
  };

  BoostBulkGrid.prototype.performDecoratedAdds = function (decoAdds) {
    var self = this;
    var feeCfg = this.getFeeConfig();

    function newKey() {
      return (self.productKeyPrefix || 'pk-') + Math.random().toString(16).slice(2, 8);
    }

    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var cartItems = (cart && cart.items) || [];
        function cartHasVariant(vid) {
          if (!vid) return false;
          return cartItems.some(function (it) {
            return Number(it.variant_id) === vid && it.quantity > 0;
          });
        }

        var items = [];
        // Track whether, across this batch, we still need to inject each fee.
        // Start from current cart presence; once we include a fee in this
        // batch, don't include it again.
        var artworkFeePresent = cartHasVariant(feeCfg.artworkFeeVariantId);
        var embFeePresent = cartHasVariant(feeCfg.embroideryFeeVariantId);

        Object.keys(decoAdds).forEach(function (addKey) {
          var entry = decoAdds[addKey];
          if (!entry || !entry.garmentVariantId || entry.quantity <= 0) return;
          var pk = newKey();
          var thisHasEmbroidery = false;
          // BOOST CUSTOMISATION 2026-05-22 (Fixes Round 2): garment label for
          // the visible "For garment" property on all carrier lines.
          var garmentLabel = entry.garmentLabel || '';

          // Fee elements first (so they sink to the group bottom under LIFO):
          // $50 embroidery fee if this add includes embroidery and none yet.
          entry.carriers.forEach(function (c) {
            if (c.method === 'embroidery') thisHasEmbroidery = true;
          });
          if (thisHasEmbroidery && feeCfg.embroideryFeeVariantId && !embFeePresent) {
            items.push({
              id: feeCfg.embroideryFeeVariantId,
              quantity: 1,
              properties: { _fee_type: 'embroidery_setup' }
            });
            embFeePresent = true;
          }
          // $70 artwork fee if any decoration and none yet.
          if (feeCfg.artworkFeeVariantId && !artworkFeePresent) {
            items.push({
              id: feeCfg.artworkFeeVariantId,
              quantity: 1,
              properties: { _fee_type: 'artwork_setup' }
            });
            artworkFeePresent = true;
          }

          // Carriers in REVERSE rank (so they render in rank order under the
          // garment), matching the picker's LIFO array convention. carriers
          // are already rank-sorted (from decorationsForClone), so iterate
          // backwards.
          for (var i = entry.carriers.length - 1; i >= 0; i--) {
            var decProps = {
              _product_key: pk,
              _product_option: 'decoration',
              _decoration_method: entry.carriers[i].method
            };
            // BOOST CUSTOMISATION 2026-05-22 (Fixes Round 2): visible "For
            // garment" property — no underscore prefix so Shopify renders it
            // at checkout and in Order Printer Pro. Themed cart suppresses the
            // raw property row (carriers are filtered from the themed render).
            if (garmentLabel) decProps['For garment'] = garmentLabel;
            items.push({
              id: Number(entry.carriers[i].variantId),
              quantity: entry.quantity,
              properties: decProps
            });
          }

          // Garment LAST (=> floats to group top under LIFO).
          items.push({
            id: entry.garmentVariantId,
            quantity: entry.quantity,
            properties: { _product_key: pk }
          });
        });

        if (items.length === 0) return null;

        return fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items, sections: 'side-cart' })
        }).then(function (r) { return r.json(); })
          .then(function (response) {
            self.applyDrawerSection(response, false);
            document.dispatchEvent(new CustomEvent('boost:cart-updated', { detail: { source: 'bulk-grid-decorated-add' } }));
          });
      });
  };

  /* ============================================
  BOOST CUSTOMISATION 2026-05-23 (KEY-ADDRESSED REWRITE + STATE/RENDER SPLIT):
  submissionGuard — wraps a promise-returning fn so the grid is marked
  in-flight for its duration (submitting class + submissionInFlight). Uses a
  DEPTH COUNTER (_submitDepth) rather than a bare boolean so that nested or
  sequential guards, plus the flush-level cycle claim, compose correctly:
  submissionInFlight is true whenever depth > 0 and only returns to false when
  the outermost scope completes. This stops an inner fetch's completion from
  prematurely releasing the cycle-wide guard the flush holds across its server
  call + state rebuild.
  ============================================ */
  BoostBulkGrid.prototype._enterSubmission = function () {
    this._submitDepth = (this._submitDepth || 0) + 1;
    this.submissionInFlight = true;
    this.root.classList.add('boost-bulk-grid-submitting');
  };
  BoostBulkGrid.prototype._exitSubmission = function () {
    this._submitDepth = Math.max(0, (this._submitDepth || 0) - 1);
    if (this._submitDepth === 0) {
      this.submissionInFlight = false;
      this.root.classList.remove('boost-bulk-grid-submitting');
    }
  };
  BoostBulkGrid.prototype.submissionGuard = function (fn) {
    var self = this;
    self._enterSubmission();
    return Promise.resolve()
      .then(function () { return fn(); })
      .then(function (result) {
        self._exitSubmission();
        return result;
      })
      .catch(function (err) {
        self._exitSubmission();
        throw err;
      });
  };

  BoostBulkGrid.prototype.fireCartUpdate = function (updatesArray, openDrawer, callback) {
    var self = this;
    self._enterSubmission();
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updatesArray, sections: 'side-cart' })
    })
      .then(function (r) { return r.json(); })
      .then(function (response) {
        self.applyDrawerSection(response, openDrawer);
        // V2.1: dispatch themeCartOpened to force the drawer open. The
        // ajaxCart.load() openCartPanel arg can be unreliable when the
        // drawer state isn't fresh; this event call is the actual
        // mechanism used by showCartPanel internally.
        if (openDrawer) {
          try { window.dispatchEvent(new CustomEvent('themeCartOpened')); }
          catch (e) { /* swallow */ }
        }
        document.dispatchEvent(new CustomEvent('boost:cart-updated', { detail: { source: 'bulk-grid' } }));
        if (callback) callback();
      })
      .catch(function (err) {
        console.error('[boost-bulk-grid] /cart/update.js failed:', err);
      })
      .then(function () {
        self._exitSubmission();
      });
  };

  BoostBulkGrid.prototype.applyDrawerSection = function (response, openDrawer) {
    var hasSection = response && response.sections && response.sections['side-cart'];
    if (window.ajaxCart && typeof window.ajaxCart.load === 'function' && hasSection) {
      try { window.ajaxCart.load(response, !!openDrawer, false, false); }
      catch (e) { /* swallow */ }
    }
  };

  function patchFetchAndXhr(onCartMutation) {
    var origFetch = window.fetch;
    if (typeof origFetch === 'function') {
      window.fetch = function () {
        var args = arguments;
        var url = (args[0] && args[0].url) || args[0] || '';
        url = String(url);
        var isCartMutation = /\/cart\/(add|update|change|clear)\.js/.test(url);
        var promise = origFetch.apply(this, args);
        if (isCartMutation) {
          promise.then(function () { setTimeout(onCartMutation, POST_MUTATION_RESYNC_DELAY); });
        }
        return promise;
      };
    }
    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__boostUrl = String(url || '');
      return origOpen.apply(this, arguments);
    };
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      var url = this.__boostUrl || '';
      if (/\/cart\/(add|update|change|clear)\.js/.test(url)) {
        this.addEventListener('load', function () {
          setTimeout(onCartMutation, POST_MUTATION_RESYNC_DELAY);
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  function formatMoney(cents, currency) {
    var dollars = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var symbol = '$';
    if (['AUD', 'USD', 'CAD', 'NZD', 'SGD'].indexOf(currency) === -1) symbol = currency + ' ';
    return symbol + dollars;
  }

  function init() {
    var roots = document.querySelectorAll('[data-boost-bulk-grid]');
    if (roots.length === 0) return;
    var grids = [];
    roots.forEach(function (root) { grids.push(new BoostBulkGrid(root)); });
    patchFetchAndXhr(function () {
      grids.forEach(function (g) { g.refreshFromCart(); });
    });
    ['ajaxCart', 'themeCartOpened', 'listCart'].forEach(function (evtName) {
      window.addEventListener(evtName, function () {
        grids.forEach(function (g) { g.refreshFromCart(); });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================
BOOST CUSTOMISATION END
============================================ */
